<template>
  <div
    id="onboarding-view"
    class="tw-flex tw-flex-col tw-items-center tw-items-center tw-min-h-screen"
  >
    <Loader v-if="loading" />
    <header
      class="tw-bg-[#FAFBFF] tw-h-[82px] tw-w-full tw-border tw-items-center tw-flex tw-justify-between tw-px-8 md:tw-px-[50px]"
    >
      <a
        href="http://getequity.io"
        target="_blank"
        rel="noopener noreferrer"
        :class="{ 'tw-mx-auto': organisationDetails }"
      >
        <img src="@/assets/img/logo-name.svg" alt="getEquity" />
      </a>
      <template v-if="$route.name !== 'SignIn'">
        <Popper offsetDistance="8">
          <button class="signin-dropdown-trigger">
            <span>Sign In</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <template #content>
            <div class="signin-dropdown">
              <a
                :href="windowLocation.origin.includes('getequity.io') ? 'https://app.getequity.io/onboarding/signin' : 'https://staging--getequityinvestors.netlify.app/onboarding/signin'"
                target="_blank"
                rel="noopener noreferrer"
                class="signin-dropdown__item"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <circle cx="12" cy="7" r="4" stroke="currentColor" stroke-width="2"/>
                </svg>
                <span>Sign in as Investor</span>
              </a>
              <router-link :to="{ name: 'SignIn' }" class="signin-dropdown__item">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M3 21H21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M5 21V7L13 3V21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M19 21V11L13 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>Sign in as Business</span>
              </router-link>
              <a
                :href="windowLocation.origin.includes('getequity.io') ? 'https://dealroom.getequity.io/onboarding/signin' : 'https://staging--dealroom.netlify.app/onboarding/signin'"
                target="_blank"
                rel="noopener noreferrer"
                class="signin-dropdown__item"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M2 17L12 22L22 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M2 12L12 17L22 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <span>Sign in as Asset Manager</span>
              </a>
            </div>
          </template>
        </Popper>
      </template>
      <template v-else>
        <router-link :to="{ name: 'GetStarted' }">
          <button
            class="tw-bg-primary tw-leading-[140%] tw-font-normal tw-text-white tw-text-base tw-rounded tw-w-[150px] tw-h-[45px] tw-block"
          >
            Get Started
          </button>
        </router-link>
      </template>
    </header>
    <section
      class="tw-pt-24"
      :class="[
        $route.name === 'AddBank'
          ? 'md:tw-pt-8 lg:tw-pt-10'
          : 'lg:tw-pt-32 md:tw-pt-20',
      ]"
    >
      <router-view v-slot="{ Component }">
        <transition name="fade-transform" mode="out-in">
          <component :is="Component" class="tw-px-4" />
        </transition>
      </router-view>
    </section>
  </div>
</template>

<script>
import Loader from "@/components/notification/Loader";
import { getItem } from "@/utils/storage.js";
import { mapGetters } from "vuex";
import Popper from "vue3-popper";

export default {
  name: "OnboardingView",
  components: { Loader, Popper },
  computed: {
    ...mapGetters(["loading"]),
    organisationDetails() {
      const org = getItem("org_details");
      return org;
    },
    windowLocation() {
      return window.location;
    },
  },
};
</script>

<style lang="scss" scoped>
#onboarding-view {
  @include fadeIn;
}

.full-width {
  width: 80%;
}

.signin-dropdown-trigger {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
  background: #4D84FF;
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #3B6FE8;
    transform: translateY(-1px);
  }

  svg {
    transition: transform 0.2s ease;
  }
}

.signin-dropdown {
  min-width: 220px;
  padding: 8px;
  background: white;
  border-radius: 12px;
  border: 1px solid #E2E8F0;
  box-shadow: 0 10px 40px rgba(15, 23, 42, 0.12);

  &__item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border-radius: 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px;
    font-weight: 400;
    color: #0F172A;
    text-decoration: none;
    transition: all 0.15s ease;

    svg {
      color: #64748B;
      flex-shrink: 0;
      transition: color 0.15s ease;
    }

    &:hover {
      background: #F8FAFC;
      color: #4D84FF;

      svg {
        color: #4D84FF;
      }
    }
  }
}
</style>
